// @ts-strict-ignore
import { Component, EventEmitter, HostBinding, OnDestroy, OnInit, Output } from '@angular/core';
import { FavoriteArticlesFilter } from '@project/components/article/presenter/favorite-articles-filter';
import { ArticleCollectionVO, ArticleVO } from '@article/model/vo/article.vo';
import { LocalizedPropertyService } from '@shared/localization/localized-property.service';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ArticlePreviewUtils } from '@article/util/article-preview.utils';
import { ArticleUtils } from '@article/util/article-utils';
import { UserModel } from '@user/model/user.model';
import { ArticleNumberMappingVO } from '@article/model/vo/article-number-mapping.vo';
import { ArticleNumberMappingUtils } from '@article/util/article-number-mapping-utils';
import { LocaleUtils } from '@shared/util/locale-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { CollectionService } from '@user/components/favorites/service/collection.service';
import { ArticleTypeManager } from '../manager/article-type.manager';

@Component({
  standalone: false,
  selector: 'select-article',
  templateUrl: './select-article.component.html',
})
export class SelectArticleComponent implements OnInit, OnDestroy {
  @HostBinding(`class.d-flex`) displayFlexClass = true;
  @HostBinding(`class.flex-column`) flexDirectionClass = true;
  @HostBinding(`style.min-height.px`) minHeight = 330;

  private favoriteArticlesFilter: FavoriteArticlesFilter;

  public articles: ArticleVO[];
  public sourceArticles: ArticleVO[];
  public useFavorites: boolean = false;
  public useFavoritesCheckBoxVisible: boolean = true;
  public ignoreSilentClose: boolean = true;
  public isCloseEventEmitted: boolean = false;
  public titlePrompt: string;
  public headerPrompt: string;

  @Output() public finish = new EventEmitter<ArticleVO | null | undefined>();

  public currentSelectedArticle: ArticleVO;

  constructor(
    private bsModalRef: BsModalRef,
    private userModel: UserModel,
    private localeResourceManager: LocaleResourceManager,
    private collectionService: CollectionService,
    private articleTypeManager: ArticleTypeManager,
    private localizedPropertyService: LocalizedPropertyService,
  ) {}

  public ngOnInit(): void {
    this.favoriteArticlesFilter = new FavoriteArticlesFilter(this.userModel, this.articleTypeManager);
    this.favoriteArticlesFilter.loadFavoriteTypeIds().subscribe(() => this.updateSourceArticles());
  }

  public ngOnDestroy(): void {
    if (!this.ignoreSilentClose && !this.isCloseEventEmitted) {
      this.isCloseEventEmitted = true;
      this.finish.emit();
    }
  }

  public close(article: ArticleVO = null): void {
    this.bsModalRef.hide();
    this.isCloseEventEmitted = true;
    this.finish.emit(article);
  }

  public skip(): void {
    this.bsModalRef.hide();
    this.isCloseEventEmitted = true;
    this.finish.emit();
  }

  public isArticleInCollection(articleId: number): boolean {
    return this.collectionService.isArticleInCollection(articleId);
  }

  public selectItem(article: ArticleVO): void {
    this.currentSelectedArticle = article;
  }

  public getArticleImage(article: ArticleVO): string {
    return ArticlePreviewUtils.getPreviewImage(article);
  }

  public getArticleNumber(article: ArticleVO): string {
    if (!article) {
      return '';
    }
    if (ArticleUtils.isArticleNumberHidden(article) && !this.userModel.user.hasPermission("SHOW_HIDDEN_ARTICLE_NUMBERS")) {
      return '';
    } else {
      return article.articleNo;
    }
  }

  public getCustomArticleNumber(article: ArticleVO): string {
    if (!article) {
      return '';
    }
    const mapping: ArticleNumberMappingVO = ArticleNumberMappingUtils.findMappingByArticleNumber(
      article.articleNo,
      this.userModel.articleNumberMappings
    );
    return !mapping ? '' : mapping.customArticleNumber;
  }

  public getArticleColorValue(article: ArticleVO): string {
    return this.localizedPropertyService.getColorNameFromArticle(article);
  }

  public getArticleDescription(article: ArticleVO): string {
    if (!article) {
      return '';
    }
    const displayName: string = LocaleUtils.getValueByLocaleChain(article.displayName, this.localeResourceManager.getLocaleChain());
    return !displayName ? '' : displayName;
  }

  public async addArticleToArticleCollection(article: ArticleVO): Promise<void> {
    const articleCollection = await this.collectionService.addArticleToArticleCollection(article.identity);
    if (articleCollection) {
      if (articleCollection.type === ArticleCollectionVO.TYPE_FAVORITES) {
        this.favoriteArticlesFilter.storeFavoriteArticle(article);
        if (this.useFavorites) {
          this.updateSourceArticles();
        }
      }
    }
  }

  public selectArticle(article: ArticleVO): void {
    if (article) {
      this.currentSelectedArticle = article;
    }
    if (this.currentSelectedArticle) {
      this.close(this.currentSelectedArticle);
    }
  }

  public updateSourceArticles(): void {
    const newSourceArticles: ArticleVO[] = this.useFavorites ? this.favoriteArticlesFilter.filterArticles(this.articles) : this.articles;
    this.sourceArticles = newSourceArticles;
  }
}
